import React from "react";
import { Icon } from "../../ui/UIElements/Icon";
import { Button } from "../../ui/UIElements/Button";
import { ContextHelp } from "../../ui";
import { convertTime, useGlobalUtils } from "../../../lib/globalUtils";
import { useTranslation } from "react-i18next";
// import { getConversationIcon } from '.';

const toReadableLabel = (value) => {
  if (!value) return "";

  return String(value)
    .split(/[:_]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" / ");
};

/**
 * ConversationCard component for displaying individual conversation information
 * @param {Object} props - Component props
 * @param {Object} props.conversation - Conversation data from API
 * @param {Function} props.onViewChat - Callback when view chat is clicked
 * @param {Function} props.onViewOrder - Callback when view order is clicked (if order exists)
 */
const ConversationCard = ({ conversation, onViewChat, onViewOrder }) => {
  const { t } = useTranslation("conversations");
  const { parseDate, parsePrice } = useGlobalUtils();

  const {
    status,
    status_reason,
    type,
    order,
    last_message_at,
    created_at,
    messages_count,
    user,
    usage,
    business_id,
    business,
    agent,
  } = conversation;

  // Get user display name
  const userName = user?.name;
  const userContact = user?.cellphone
    ? [
        user?.country_phone_code ? `+${user?.country_phone_code}` : "",
        user?.cellphone,
      ]
        .filter(Boolean)
        .join(" ")
    : user?.email;

  // Get status color and styling
  const getStatusStyle = (status) => {
    switch (status) {
      case "active":
        return "border-success/40 bg-success-bg text-success";
      case "writing":
        return "border-primary/40 bg-accent text-primary";
      default:
        return "border-border bg-muted text-muted-foreground";
    }
  };

  // Get status_reason color and styling
  const getStatusReasonStyle = (status_reason) => {
    switch (status_reason) {
      case "completed":
        return "border-success/40 bg-success-bg text-success";
      case "abandoned":
        return "border-warning/40 bg-warning-bg text-warning";
      case "failed":
        return "border-error/40 bg-error-bg text-error";
      default:
        return "border-border bg-muted text-muted-foreground";
    }
  };

  const orderId = order?.id;
  const orderTotal = order?.summary?.total || 0;
  const statusLabel = t(`status.${status}`, { defaultValue: status });
  const statusReasonLabel = status_reason
    ? t(`statusReason.${status_reason}`, {
        defaultValue: status_reason,
      })
    : "";
  const assignedBusinessName = business?.name;
  const assignedBusinessNameFallback =
    conversation?.business_name ||
    conversation?.businessName ||
    conversation?.assigned_business_name;
  const resolvedAssignedBusinessName =
    assignedBusinessName || assignedBusinessNameFallback;
  const assignedBusinessLabel = assignedBusinessName
    ? assignedBusinessName
    : resolvedAssignedBusinessName
      ? resolvedAssignedBusinessName
      : business_id
        ? t("card.notAvailable")
        : t("card.notAssigned");

  const rawAgentType =
    agent?.provider_type || agent?.type || agent?.front_type || type;
  const rawAgentProvider = agent?.provider;
  const formattedAgentType = toReadableLabel(rawAgentType);
  const formattedAgentProvider = toReadableLabel(rawAgentProvider);
  const agentTypeParts = [];

  [formattedAgentProvider, formattedAgentType].forEach((part) => {
    if (!part) return;

    const isDuplicate = agentTypeParts.some(
      (existingPart) => existingPart.toLowerCase() === part.toLowerCase(),
    );

    if (!isDuplicate) {
      agentTypeParts.push(part);
    }
  });

  const assignedAgentTypeLabel =
    agentTypeParts.join(" / ") || t("card.notAssigned");

  const channelIcons = {
    whatsapp: {
      label: "WhatsApp",
      bg: "bg-success text-success-foreground",
      svg: (
        <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
        </svg>
      ),
    },
    chat: {
      label: "Web Chat",
      bg: "bg-primary text-primary-foreground",
      svg: <Icon name="MessageCircle" className="h-6 w-6" />,
    },
    default: {
      label: "Web",
      bg: "bg-secondary text-secondary-foreground",
      svg: <Icon name="Monitor" className="h-6 w-6" />,
    },
  };

  const channelTypeMap = {
    whatsapp: "whatsapp",
    "whatsapp:chat": "whatsapp",
    "ordering:chat": "chat",
    "finitless:chat": "chat",
    ordering: "chat",
  };

  const getConversationIcon = (type) => {
    const channel = channelIcons[channelTypeMap[type]] || channelIcons.default;
    return (
      <div
        className="group/icon relative"
        data-conversation-channel={channelTypeMap[type] || "default"}
      >
        <div
          className={`flex h-12 w-12 items-center justify-center rounded-lg ${channel.bg}`}
        >
          {channel.svg}
        </div>
        <div className="bg-popover text-popover-foreground border-border pointer-events-none absolute start-1/2 -top-8 z-[var(--layer-popover)] -translate-x-1/2 rounded-md border px-2 py-1 text-xs whitespace-nowrap opacity-0 shadow-[var(--od-shadow-2)] transition-opacity group-hover/icon:opacity-100 motion-reduce:transition-none rtl:translate-x-1/2">
          {channel.label}
        </div>
      </div>
    );
  };

  return (
    <article
      className="ordering-conversation-card border-border bg-card flex min-w-0 flex-col justify-between rounded-lg border p-4 shadow-[var(--od-shadow-1)] transition-colors hover:border-[var(--conversation-state)] motion-reduce:transition-none"
      data-conversation-status={status || "unknown"}
      data-conversation-reason={status_reason || "none"}
    >
      <div className="flex flex-col">
        {/* Header */}
        <div className="mb-3 flex min-w-0 flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <div className="flex-shrink-0 rounded-lg">
              {getConversationIcon(type)}
            </div>
            <div className="min-w-0 flex-1">
              {userName && (
                <h3 className="text-foreground font-medium break-words">
                  {userName}
                </h3>
              )}
              {userContact && (
                <p className="text-muted-foreground text-sm break-all">
                  {userContact}
                </p>
              )}
            </div>
          </div>
          <div className="ms-auto flex flex-col items-end gap-1">
            <span
              className={`rounded-md border px-2 py-1 text-xs font-medium capitalize ${getStatusStyle(
                status,
              )}`}
            >
              {statusLabel}
            </span>
            {status_reason && (
              <span
                className={`rounded-md border px-2 py-1 text-xs font-medium capitalize ${getStatusReasonStyle(
                  status_reason,
                )}`}
              >
                {statusReasonLabel}
              </span>
            )}
          </div>
        </div>

        {/* Business Info */}
        {business && (
          <div className="mb-2.5">
            <div className="flex min-w-0 items-center gap-2">
              {business.logo ? (
                <img
                  src={business.logo}
                  alt={business.name || t("card.business")}
                  className="h-5 w-5 flex-shrink-0 rounded-sm object-cover"
                />
              ) : (
                <Icon
                  name="Store"
                  className="text-muted-foreground h-5 w-5 flex-shrink-0"
                />
              )}
              <div className="min-w-0 flex-1">
                <span className="text-foreground block text-xs font-medium break-words sm:text-sm">
                  {business.name || t("card.notAvailable")}
                </span>
              </div>
            </div>
          </div>
        )}

        <div className="border-border bg-muted mb-3 rounded-md border p-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="min-w-0">
              <p className="text-muted-foreground text-xs">
                {t("card.labels.assignedBusiness")}
              </p>
              <p
                className="text-foreground text-sm font-medium break-words"
                title={assignedBusinessLabel}
              >
                {assignedBusinessLabel}
              </p>
            </div>
            <div className="min-w-0">
              <p className="text-muted-foreground text-xs">
                {t("card.labels.agentType")}
              </p>
              <p
                className="text-foreground text-sm font-medium break-words"
                title={assignedAgentTypeLabel}
              >
                {assignedAgentTypeLabel}
              </p>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="mb-3 items-center justify-between">
          <div className="mb-2 flex flex-wrap items-center gap-4">
            <div className="flex min-w-0 items-center gap-1">
              <Icon
                name="Clock"
                className="text-muted-foreground h-4 w-4 flex-shrink-0"
              />
              <span className="text-muted-foreground text-sm break-words">
                {parseDate(last_message_at || created_at, {
                  outputFormat: "dd/MM/yyyy HH:mm",
                })}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <Icon
                name="MessageSquare"
                className="text-muted-foreground h-4 w-4"
              />
              <span className="text-foreground text-sm">
                {t("card.labels.messages")}: {messages_count || 0}
              </span>
            </div>
          </div>
          <div className="mb-2 flex flex-wrap items-center gap-3">
            {!!usage?.average_response_time_ms && (
              <div className="flex min-w-0 items-center gap-1">
                <Icon
                  name="Clock"
                  className="text-muted-foreground h-4 w-4 flex-shrink-0"
                />
                <span className="text-muted-foreground flex min-w-0 flex-wrap items-center gap-1 text-sm">
                  {t("card.labels.responseTime")}:{" "}
                  {convertTime(
                    usage?.average_response_time_ms || 0,
                    "ms",
                    "s",
                    { decimals: 1, includeUnit: true },
                  )}
                  <ContextHelp
                    className="h-3.5 w-3.5"
                    ariaLabel={t("help.card.responseTime.ariaLabel")}
                    whatIs={t("help.card.responseTime.whatIs")}
                    period={t("help.card.responseTime.period")}
                  />
                </span>
              </div>
            )}
            {status_reason === "completed" &&
              usage?.roi != null &&
              usage.roi >= 0 && (
                <div className="flex items-center gap-1">
                  <Icon
                    name="ChartBar"
                    className="text-muted-foreground h-4 w-4"
                  />
                  <span className="text-muted-foreground text-sm">
                    {t("card.labels.roiValue", { value: usage.roi.toFixed(2) })}
                  </span>
                </div>
              )}
            {usage?.cost != null && usage.cost >= 0 && (
              <div className="flex min-w-0 items-center gap-1">
                <Icon
                  name="DollarSign"
                  className="text-muted-foreground h-4 w-4 flex-shrink-0"
                />
                <span className="text-muted-foreground text-sm break-words">
                  {parsePrice(usage.cost)}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Business Assignment Information */}
      {!business_id && (
        <div className="border-warning/40 bg-warning-bg mb-3 rounded-md border p-3">
          <div className="flex items-center gap-2">
            <Icon
              name="AlertCircle"
              className="text-warning h-4 w-4 flex-shrink-0"
            />
            <div className="flex items-center gap-1">
              <p className="text-warning text-sm font-medium">
                {t("card.businessNotAssigned")}
              </p>
              <ContextHelp
                className="h-3.5 w-3.5"
                ariaLabel={t("help.card.businessNotAssigned.ariaLabel")}
                whatIs={t("help.card.businessNotAssigned.whatIs")}
                note={t("help.card.businessNotAssigned.note")}
              />
            </div>
          </div>
        </div>
      )}
      {/* Order Information */}
      {orderId ? (
        <div className="border-success/40 bg-success-bg mb-3 rounded-md border p-3">
          <div className="flex min-w-0 flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-success text-sm font-medium">
                {t("card.orderCreated")}
              </p>
              <p className="text-foreground text-sm break-words">
                {orderId
                  ? t("card.orderNumber", { id: orderId })
                  : t("card.orderCreated")}
                {orderTotal && ` - ${parsePrice(orderTotal)}`}
              </p>
            </div>
            {onViewOrder && (
              <Button
                variant="ghost"
                type="button"
                onClick={() => onViewOrder(orderId)}
                className="border-success/40 bg-card text-success hover:bg-success-bg focus-visible:ring-ring min-h-11 rounded-md border px-3 py-2 text-xs transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none motion-reduce:transition-none"
              >
                {t("card.actions.viewOrder")}
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div className="border-warning/40 bg-warning-bg mb-3 rounded-md border p-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-1">
                <p className="text-warning text-sm font-medium">
                  {t("card.orderIncomplete")}
                </p>
                <ContextHelp
                  className="h-3.5 w-3.5"
                  ariaLabel={t("help.card.orderIncomplete.ariaLabel")}
                  whatIs={t("help.card.orderIncomplete.whatIs")}
                  note={t("help.card.orderIncomplete.note")}
                />
              </div>
              <p className="text-foreground text-sm">
                {t("card.noOrderCreated")}
              </p>
            </div>
          </div>
        </div>
      )}
      {/* View Chat Button */}
      <Button
        variant="ghost"
        type="button"
        onClick={() => onViewChat(conversation)}
        className="bg-primary text-primary-foreground focus-visible:ring-ring flex min-h-11 w-full items-center justify-center gap-2 rounded-md px-4 py-2 font-medium transition-colors hover:bg-[var(--od-action-hover)] focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none motion-reduce:transition-none"
      >
        <Icon name="MessageCircle" className="h-4 w-4" />
        <span>{t("card.actions.viewChat")}</span>
      </Button>
    </article>
  );
};

export default ConversationCard;
